import { useState, useEffect } from "react";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useExpansion } from "@/contexts/expansion-context";
import { useToast } from "@/hooks/use-toast";
import { useScrollPreservation } from "@/hooks/use-scroll-preservation";
import { usePulseById } from "@/hooks/useProjectTreeData";
import { addDays, isWeekend } from 'date-fns';
import { TREE_GRID_TEMPLATE, TREE_GRID_TEMPLATE_WITH_BUDGET, getTreeColumnStyles } from '@/lib/tree-columns';

interface PulseTasksLoaderProps {
  pulseId: number;
  projectId: string;
  renderTaskTree: (tasks: any[], projectId: string, depth: number) => React.ReactNode;
  shouldStartAddingTask?: boolean;
  onAddingTaskComplete?: () => void;
  toggleExpanded: (id: string) => void;
  isProjectDeactivated?: boolean;
  showBudgetColumn?: boolean;
}

export function PulseTasksLoader({
  pulseId,
  projectId,
  renderTaskTree,
  shouldStartAddingTask,
  onAddingTaskComplete,
  toggleExpanded,
  isProjectDeactivated = false,
  showBudgetColumn = false
}: PulseTasksLoaderProps) {
  const { expandedItems: globalExpandedItems, setExpanded } = useExpansion();
  
  // Get pulse data from tree FIRST
  const pulseFromTree = usePulseById(pulseId) as (ReturnType<typeof usePulseById> & { tasks?: any[] }) | undefined;
  const hasTreeTaskData = pulseFromTree?.tasks !== undefined;
  
  // Only query individual pulse if tree doesn't have task data
  const { data: pulseWithTasks, isLoading } = useQuery<{ tasks?: any[] }>({
    queryKey: ['/api/pulses', pulseId, 'details'],
    enabled: !hasTreeTaskData, // ONLY query if tree doesn't have tasks
    refetchOnMount: 'always',
    staleTime: 0
  });
  
  const [isAddingTask, setIsAddingTask] = useState(false);
  const [newTaskDescription, setNewTaskDescription] = useState("");
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { captureScroll, restoreScroll } = useScrollPreservation();

  useEffect(() => {
    if (shouldStartAddingTask && !isAddingTask && !pulseWithTasks?.tasks?.length) {
      setIsAddingTask(true);
    }
  }, [shouldStartAddingTask, isAddingTask, pulseWithTasks?.tasks?.length]);

  function getNextWorkday() {
    let date = new Date();
    let businessDaysAdded = 0;
    
    while (businessDaysAdded < 2) {
      date = addDays(date, 1);
      if (!isWeekend(date)) {
        businessDaysAdded++;
      }
    }
    
    return date;
  }

  const createTaskMutation = useMutation({
    mutationFn: async (taskDescription: string) => {
      const taskData = {
        projectId,
        pulseId,
        taskDescription,
        status: 'go',
        dueDate: getNextWorkday().toISOString(),
        category: 'internal' as const
      };
      
      const response = await apiRequest('POST', '/api/tasks', taskData);

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.details || errorData.error || 'Failed to create task');
      }

      return response.json();
    },
    onMutate: async () => {
      captureScroll();
      
      await queryClient.cancelQueries({ queryKey: ['/api/years'] });
      await queryClient.cancelQueries({ queryKey: ['/api/projects'] });
      await queryClient.cancelQueries({ queryKey: ['/api/pulses/batch-details'] });
      await queryClient.cancelQueries({ queryKey: ['/api/pulses/batch-metadata'] });
      await queryClient.cancelQueries({ queryKey: ['/api/pulses/batch/summary'] });
      await queryClient.cancelQueries({ queryKey: ['/api/pulses', pulseId, 'details'] });
      await queryClient.cancelQueries({ queryKey: ['/api/pulses', pulseId, 'has-tasks'] });
      
      const previousHasTasks = queryClient.getQueryData(['/api/pulses', pulseId, 'has-tasks']);
      const previousDetails = queryClient.getQueryData(['/api/pulses', pulseId, 'details']);
      const previousYears = queryClient.getQueryData(['/api/years']);
      
      queryClient.setQueryData(['/api/pulses', pulseId, 'has-tasks'], { hasTasks: true });
      
      return { previousHasTasks, previousDetails, previousYears };
    },
    onSuccess: (createdTask) => {
      queryClient.setQueryData(['/api/pulses', pulseId, 'details'], (old: any) => {
        if (!old) return old;
        return {
          ...old,
          tasks: [...(old.tasks || []), createdTask]
        };
      });
      
      const batchDetailsQueries = queryClient.getQueriesData({ 
        queryKey: ['/api/pulses/batch-details'] 
      });
      
      batchDetailsQueries.forEach(([queryKey, queryData]) => {
        if (queryData && Array.isArray(queryData)) {
          const keyArray = queryKey as any[];
          const pulseIds = keyArray[1];
          
          if (Array.isArray(pulseIds) && pulseIds.includes(pulseId)) {
            const updatedData = queryData.map((pulse: any) => 
              pulse.id === pulseId 
                ? { ...pulse, taskCounts: { ...pulse.taskCounts, total: (pulse.taskCounts?.total || 0) + 1 } }
                : pulse
            );
            queryClient.setQueryData(queryKey, updatedData);
          }
        }
      });
      
      queryClient.setQueryData(['/api/years'], (old: any) => {
        if (!old) return old;
        
        return old.map((year: any) => ({
          ...year,
          projects: year.projects?.map((proj: any) => {
            if (proj.id === projectId) {
              return {
                ...proj,
                pulses: proj.pulses?.map((pulse: any) => {
                  if (pulse.id === pulseId) {
                    return {
                      ...pulse,
                      tasks: [...(pulse.tasks || []), createdTask]
                    };
                  }
                  return pulse;
                })
              };
            }
            return proj;
          })
        }));
      });
      
      setIsAddingTask(false);
      setNewTaskDescription("");
      
      if (onAddingTaskComplete) {
        onAddingTaskComplete();
      }
      
      setTimeout(() => {
        restoreScroll();
      }, 50);
    },
    onError: (error: any) => {
      toast({
        title: "Error creating task",
        description: error.message,
        variant: "destructive"
      });
      setIsAddingTask(false);
      setTimeout(() => {
        restoreScroll();
      }, 50);
    }
  });

  const handleCreateTask = async () => {
    if (!newTaskDescription.trim()) return;
    await createTaskMutation.mutateAsync(newTaskDescription);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleCreateTask();
    } else if (e.key === 'Escape') {
      setIsAddingTask(false);
      setNewTaskDescription("");
      if (onAddingTaskComplete) {
        onAddingTaskComplete();
      }
    }
  };

  if (isLoading && !hasTreeTaskData) {
    return (
      <div className="tree-item border-b border-gray-600 tree-indent-2">
        <div className="flex items-center h-12 px-3">
          <div className="text-sm text-gray-500">Loading tasks...</div>
        </div>
      </div>
    );
  }

  // Use tree data if available, otherwise use query result
  const tasks = pulseFromTree?.tasks || pulseWithTasks?.tasks || [];

  return (
    <>
      {tasks.map((task: any) => renderTaskTree([task], projectId, 1))}
      
      {/* Hide add task UI for frozen projects */}
      {!isProjectDeactivated && (
        isAddingTask ? (
          <div className="tree-item border-b border-gray-600 tree-indent-2">
            <div className="grid px-3 items-center gap-0 py-2" style={{ gridTemplateColumns: showBudgetColumn ? TREE_GRID_TEMPLATE_WITH_BUDGET : TREE_GRID_TEMPLATE, ...getTreeColumnStyles(showBudgetColumn) }}>
              {/* Thumbnail Placeholder Column */}
              <div className="flex items-center justify-center border-r border-gray-600" />
              
              {/* Expand/Collapse Column */}
              <div className="flex items-center justify-center" />
              
              {/* ID Column */}
              <div className="flex items-center" />
              
              {/* Description Column */}
              <div className="min-w-0 px-3">
                <div className="flex items-center">
                  <div className="w-5 mr-2" /> {/* Icon spacing to match tasks */}
                  <input
                    type="text"
                    value={newTaskDescription}
                    onChange={(e) => setNewTaskDescription(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Enter task description..."
                    className="flex-1 bg-gray-800 text-white px-3 py-1 rounded border border-gray-600 focus:border-blue-500 focus:outline-none text-sm"
                    autoFocus
                  />
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="tree-item border-b border-gray-600 tree-indent-2">
            <div className="grid px-3 items-center gap-0" style={{ gridTemplateColumns: showBudgetColumn ? TREE_GRID_TEMPLATE_WITH_BUDGET : TREE_GRID_TEMPLATE, ...getTreeColumnStyles(showBudgetColumn) }}>
              {/* Thumbnail Placeholder Column */}
              <div className="flex items-center justify-center border-r border-gray-600" />
              
              {/* Expand/Collapse Column */}
              <div className="flex items-center justify-center" />
              
              {/* ID Column */}
              <div className="flex items-center" />
              
              {/* Description Column */}
              <div className="min-w-0 px-3">
                <div className="flex items-center">
                  <div className="w-5 mr-2" /> {/* Icon spacing to match tasks */}
                  <button
                    onClick={() => setIsAddingTask(true)}
                    className="text-sm text-gray-500 hover:text-blue-400 transition-colors"
                    data-testid="button-add-task"
                  >
                    {tasks.length === 0 ? "+ Add first task" : "+ Add Task"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )
      )}
    </>
  );
}
